/**
 * Route guards — what has to be true before a screen mounts.
 *
 * They run in `beforeLoad`, so nobody ever sees a screen they cannot open.
 * They use `ensureQueryData`: the query is reused by the screen right after,
 * with no second request.
 */
import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { trpc } from '@/lib/trpc'

type GuardArgs = { context: { queryClient: QueryClient } }

/**
 * Without an active plan there is no product to use — the person goes and
 * picks one.
 *
 * An administrator walks straight through: the house's own operation cannot
 * stop over a subscription.
 */
export const requireSubscription = async ({ context }: GuardArgs) => {
  const user = await context.queryClient.ensureQueryData(trpc.auth.me.queryOptions())

  if (user?.role === 'admin') return

  const subscription = await context.queryClient.ensureQueryData(trpc.billing.mySubscription.queryOptions())

  if (!subscription) throw redirect({ to: '/plans' })
}

/** Internal operations area: administrators only. */
export const requireAdmin = async ({ context }: GuardArgs) => {
  const user = await context.queryClient.ensureQueryData(trpc.auth.me.queryOptions())

  if (user?.role !== 'admin') throw redirect({ to: '/access-denied' })
}
